/**
 * Done. CPD — RouteGuard (index route "/")
 *
 * Authenticated users are redirected to /onboarding or /dashboard based on
 * survey completion. Unauthenticated users see the public Stitch v2
 * LandingPage with its own signup CTAs.
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { LandingPage } from '@/pages/LandingPage';

export function RouteGuard(): React.ReactElement {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!user.surveyCompleted) {
      navigate('/onboarding', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--stitch-surface)]">
        <svg
          className="h-10 w-10 animate-spin text-[var(--stitch-primary)]"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading…"
          role="img"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!user) {
    return <LandingPage />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--stitch-surface)]">
      <p className="text-sm text-[var(--stitch-on-surface-variant)]">Redirecting…</p>
    </div>
  );
}
